import { Injectable } from '@angular/core';
import { AppwriteClientService } from '@prism/angular-shared';
import { Account, Client, Models } from 'appwrite';
import { environment } from 'apps/prism-client/src/environments/environment';
import { Observable, ReplaySubject, firstValueFrom, from, map } from 'rxjs';

export type AppwriteAuthUser = Models.User<Models.Preferences> & {
    uid: string;
    displayName: string | null;
    photoURL: string | null;
    providerId: string;
    getIdToken: () => Promise<string>;
};

export type AppwriteAuthCredential = {
    user: AppwriteAuthUser | null;
    session: Models.Session;
};

@Injectable({ providedIn: 'root' })
export class AppwriteAuthService {
    private readonly account: Account;
    private readonly authStateSubject = new ReplaySubject<AppwriteAuthUser | null>(1);

    public readonly authState: Observable<AppwriteAuthUser | null> = this.authStateSubject.asObservable();
    public readonly user: Observable<AppwriteAuthUser | null> = this.authState;

    constructor(appwrite: AppwriteClientService) {
        this.account = new Account(appwrite.client);
        this.refreshAuthState();
    }

    public get currentUser(): Promise<AppwriteAuthUser | null> {
        return this.fetchCurrentUser();
    }

    public signInWithToken(userId: string, secret: string): Promise<AppwriteAuthCredential> {
        return this.account.createSession({ userId, secret }).then(async session => {
            const user = await this.fetchCurrentUser();
            this.authStateSubject.next(user);
            return { user, session };
        });
    }

    public signInWithEmailAndPassword(email: string, password: string): Promise<AppwriteAuthCredential> {
        return this.account.createEmailPasswordSession({ email, password }).then(async session => {
            const user = await this.fetchCurrentUser();
            this.authStateSubject.next(user);
            return { user, session };
        });
    }

    public signOut(): Promise<void> {
        // Only emit null when a session was actually deleted, so a no-op signOut doesn't race with a parallel signInWithToken.
        return this.account.deleteSession({ sessionId: 'current' })
            .then(() => {
                this.authStateSubject.next(null);
            })
            .catch(() => void 0);
    }

    public sendPasswordResetEmail(email: string): Promise<void> {
        const fallbackOrigin = typeof window !== 'undefined' ? window.location.origin : environment.appwrite.endpoint;
        const recoveryUrl = `${fallbackOrigin}/login`;
        return this.account.createRecovery({ email, url: recoveryUrl }).then(() => void 0);
    }

    public completePasswordRecovery(userId: string, secret: string, password: string): Promise<void> {
        return this.account.updateRecovery({ userId, secret, password }).then(() => void 0);
    }

    public login(email: string, password: string): Observable<Models.Session> {
        return from(this.signInWithEmailAndPassword(email, password)).pipe(
            map(credential => credential.session),
        );
    }

    public getCurrentAccount(): Observable<AppwriteAuthUser | null> {
        return from(this.currentUser);
    }

    public createJwt(): Observable<string> {
        return from(this.account.createJWT({})).pipe(
            map(response => response.jwt),
        );
    }

    public logout(): Observable<void> {
        return from(this.signOut());
    }

    private refreshAuthState(): void {
        this.fetchCurrentUser().then(user => {
            this.authStateSubject.next(user);
        });
    }

    private fetchCurrentUser(): Promise<AppwriteAuthUser | null> {
        return this.account.get()
            .then(account => this.toAuthUser(account))
            .catch(() => null);
    }

    private toAuthUser(account: Models.User<Models.Preferences>): AppwriteAuthUser {
        return {
            ...account,
            uid: account.$id,
            displayName: account.name || null,
            photoURL: null,
            providerId: 'appwrite',
            getIdToken: () => firstValueFrom(this.createJwt()),
        };
    }
}
